// Sicherung wiederherstellen — für den Kunden.
//
// Ablauf: Hat ein Kunde seine Daten zurückgesetzt und bereut es, schreibt er
// den Anbieter an und bekommt eine Sicherungsdatei. Die spielt er hier selbst
// ein: Datei wählen, sehen was drin ist und was dafür verschwindet, Passwort,
// fertig.
//
// Wer seinen heutigen Stand behalten will, legt ein zweites Konto an und
// bekommt die Datei für dieses Konto. Dann bleibt das erste, wie es ist, und
// die Firmenangaben können aus der Sicherung mitkommen.
//
// Die Warnung steht absichtlich groß und konkret da („4 Rechnungen,
// 12 Kunden werden gelöscht") statt allgemein („Daten gehen verloren") —
// wer seit dem Zurücksetzen schon wieder gearbeitet hat, muss das vor dem
// Klick sehen, nicht danach.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { useRef, useState } from 'react';
import { api, ApiFehler, token } from '../../../gemeinsam/lib/api';
import { datumZeit } from '../../../gemeinsam/lib/daten';
import { Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Umfang = Record<string, number>;

type Pruefung = {
  kennung: string;
  gueltigMinuten: number;
  sicherung: { erstelltAm: string; firma: string; format: number; umfang: Umfang; dateienFehlen: number };
  wirdGeloescht: Umfang;
  herkunft?: { anderesKonto: boolean; firmendaten: boolean };
};

type Ergebnis = {
  wiederhergestellt: Umfang; dateienZurueck: number; ausAnderemKonto?: boolean; firmendatenUebernommen?: boolean;
};

const NAMEN: Record<string, [string, string]> = {
  kunden: ['Kunde', 'Kunden'], lieferanten: ['Lieferant', 'Lieferanten'], artikel: ['Artikel', 'Artikel'],
  rechnungen: ['Rechnung', 'Rechnungen'], belege: ['Beleg', 'Belege'], ausgaben: ['Ausgabe', 'Ausgaben'],
  fixkosten: ['Fixkostenposten', 'Fixkostenposten'], retouren: ['Retoure', 'Retouren'],
  aufgaben: ['Aufgabe', 'Aufgaben'], nachrichten: ['Nachricht', 'Nachrichten'], fahrten: ['Fahrt', 'Fahrten'],
  dateien: ['Datei', 'Dateien'],
};

function aufzaehlen(u: Umfang) {
  return Object.entries(u)
    .filter(([k, n]) => n > 0 && NAMEN[k])
    .map(([k, n]) => `${n} ${NAMEN[k][n === 1 ? 0 : 1]}`);
}

export function Wiederherstellung() {
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [passwort, setPasswort] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<Ergebnis | null>(null);
  const [firmendaten, setFirmendaten] = useState(true);
  const feld = useRef<HTMLInputElement>(null);

  async function dateiGewaehlt(datei: File | undefined) {
    if (!datei) return;
    setFehler(null); setPruefung(null); setFertig(null); setLaedt(true);
    try {
      const antwort = await fetch(apiUrl('/api/mandant/wiederherstellen/pruefen'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token() ?? ''}`, 'Content-Type': 'application/octet-stream' },
        body: datei,
      });
      const daten = await antwort.json().catch(() => null);
      if (!antwort.ok) {
        throw new Error(daten?.error || (antwort.status === 413
          ? 'Die Datei ist zu groß.' : 'Die Datei konnte nicht geprüft werden.'));
      }
      setPruefung(daten.data);
      setFirmendaten(!!daten.data?.herkunft?.firmendaten);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Datei konnte nicht geprüft werden.');
    } finally {
      setLaedt(false);
      if (feld.current) feld.current.value = '';
    }
  }

  async function einspielen(e: React.FormEvent) {
    e.preventDefault();
    if (!pruefung) return;
    setSendet(true); setFehler(null);
    try {
      const r = await api<Ergebnis>('/mandant/wiederherstellen', {
        methode: 'POST',
        daten: { kennung: pruefung.kennung, passwort, firmendaten: !!pruefung.herkunft?.anderesKonto && firmendaten },
      });
      setFertig(r);
      setPruefung(null);
      setPasswort('');
    } catch (e) {
      if (e instanceof ApiFehler && e.code === 'ABGELAUFEN') setPruefung(null);
      setFehler(e instanceof ApiFehler ? e.message : 'Die Wiederherstellung ist fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  const geloescht = pruefung ? aufzaehlen(pruefung.wirdGeloescht) : [];
  const enthalten = pruefung ? aufzaehlen(pruefung.sicherung.umfang) : [];
  const anderesKonto = !!pruefung?.herkunft?.anderesKonto;

  return (
    <Karte>
      <KartenKopf titel="Sicherung wiederherstellen"
                  hinweis="Für den Fall, dass Sie Ihre Daten zurückgesetzt haben und sie zurückhaben möchten" />
      <div className="space-y-4 p-5">
        {fertig ? (
          <Blickfang>
            <Hinweis art="erfolg" titel="Sicherung wiederhergestellt">
              Wieder da: {aufzaehlen(fertig.wiederhergestellt).join(', ') || 'nichts'}.
              {fertig.dateienZurueck > 0 && ` Dazu ${fertig.dateienZurueck} ${fertig.dateienZurueck === 1 ? 'Datei' : 'Dateien'} (Belege, Fotos, Anhänge).`}
              {fertig.firmendatenUebernommen && ' Die Firmenangaben wurden übernommen.'}
              {fertig.ausAnderemKonto && (
                <span className="mt-2 block text-xs">
                  Ihr anderes Konto ist unverändert. Beide Konten vergeben eigene Rechnungsnummern — schreiben Sie
                  Rechnungen am besten nur aus einem der beiden, damit keine Nummer doppelt vorkommt.
                </span>
              )}
            </Hinweis>
            <div className="mt-3">
              <Knopf variante="primaer" onClick={() => { window.location.href = '/'; }}>Zur Übersicht</Knopf>
            </div>
          </Blickfang>
        ) : !pruefung ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Haben Sie Ihre Daten zurückgesetzt? Dann schreiben Sie uns über den Support — Sie bekommen eine
              Sicherungsdatei (Endung <code className="rounded bg-slate-100 px-1 text-xs dark:bg-slate-800">.lpsicherung</code>),
              die Sie hier einspielen.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Sie möchten Ihren heutigen Stand behalten? Legen Sie ein zweites Konto an und bitten Sie den Support um
              eine Datei für dieses neue Konto. Dort eingespielt, haben Sie beide Stände — dieses Konto bleibt unverändert.
            </p>
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
              {laedt ? <Spinner klein /> : null}
              {laedt ? 'Datei wird geprüft …' : 'Sicherungsdatei auswählen'}
              <input ref={feld} type="file" accept=".lpsicherung,application/octet-stream,application/json"
                     className="hidden" disabled={laedt} onChange={e => dateiGewaehlt(e.target.files?.[0])} />
            </label>
          </>
        ) : (
          <Blickfang>
            <form onSubmit={einspielen} className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Sicherung vom {datumZeit(pruefung.sicherung.erstelltAm)}
                  {pruefung.sicherung.firma && <span className="font-normal text-slate-500"> · {pruefung.sicherung.firma}</span>}
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Enthält: {enthalten.join(', ') || 'keine Daten'}
                </p>
                {pruefung.sicherung.dateienFehlen > 0 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    {pruefung.sicherung.dateienFehlen} Dateien (Belege, Fotos, Anhänge) sind in dieser Sicherung nicht enthalten
                    und kommen nicht zurück. Die Einträge dazu schon.
                  </p>
                )}
              </div>

              {anderesKonto && (
                <Hinweis art="info" titel="Sicherung aus einem anderen Konto">
                  Die Daten werden in dieses Konto übernommen. Das Konto
                  {pruefung.sicherung.firma ? ` „${pruefung.sicherung.firma}"` : ', aus dem sie stammt,'} bleibt,
                  wie es ist.
                  {pruefung.herkunft?.firmendaten && (
                    <label className="mt-3 flex items-start gap-2 text-sm">
                      <input type="checkbox" className="mt-0.5" checked={firmendaten}
                             onChange={e => setFirmendaten(e.target.checked)} />
                      <span>
                        Firmenangaben aus der Sicherung übernehmen
                        <span className="block text-xs opacity-80">
                          Anschrift, Steuernummer, Bankverbindung, Logo und Länderprofil — für Rechnungen aus diesem Konto
                        </span>
                      </span>
                    </label>
                  )}
                </Hinweis>
              )}

              <Hinweis art={geloescht.length > 0 ? 'fehler' : 'info'}
                       titel={geloescht.length > 0 ? 'Ihr heutiger Stand wird vollständig ersetzt' : 'Nichts geht verloren'}>
                {geloescht.length > 0 ? (
                  <>
                    Alles, was Sie seit dem {datumZeit(pruefung.sicherung.erstelltAm)} angelegt haben, wird gelöscht:
                    <strong className="mt-1 block">{geloescht.join(', ')}</strong>
                  </>
                ) : (
                  'Ihr Konto enthält derzeit keine Daten — durch das Wiederherstellen geht nichts verloren.'
                )}
                <span className="mt-2 block text-xs">
                  {anderesKonto && firmendaten
                    ? 'Benutzer und Passwörter bleiben unverändert.'
                    : 'Benutzer, Passwörter und Firmenangaben bleiben unverändert.'}
                  {' '}Vom heutigen Stand wird vorher eine Sicherung angelegt und 30 Tage beim Anbieter aufbewahrt.
                </span>
              </Hinweis>

              {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

              <Feld label="Zum Bestätigen Ihr Passwort" type="password" value={passwort} required autoFocus
                    autoComplete="current-password" onChange={e => setPasswort(e.target.value)} />

              <div className="flex flex-wrap gap-2">
                <Knopf type="submit" variante="gefahr" laedt={sendet} disabled={!passwort}>
                  Sicherung wiederherstellen
                </Knopf>
                <Knopf type="button" onClick={() => { setPruefung(null); setPasswort(''); setFehler(null); }}>
                  Abbrechen
                </Knopf>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Die geprüfte Datei bleibt {pruefung.gueltigMinuten} Minuten bereit. Danach bitte erneut auswählen.
              </p>
            </form>
          </Blickfang>
        )}
      </div>
    </Karte>
  );
}
